import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScanLine, LogIn, CheckCircle2, AlertTriangle, MapPin, Calendar, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { QrScannerSheet } from "@/components/QrScannerSheet";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UFS } from "@/lib/ufs";

const STATE_FILTER_KEY = "ceap_qr_uf_filter";
const MONTH_FILTER_KEY = "ceap_qr_month_filter";
const YEAR_FILTER_KEY = "ceap_qr_year_filter";

const MONTHS = [
  { value: "1", label: "Janeiro" }, { value: "2", label: "Fevereiro" }, { value: "3", label: "Março" },
  { value: "4", label: "Abril" },   { value: "5", label: "Maio" },      { value: "6", label: "Junho" },
  { value: "7", label: "Julho" },   { value: "8", label: "Agosto" },    { value: "9", label: "Setembro" },
  { value: "10", label: "Outubro" },{ value: "11", label: "Novembro" }, { value: "12", label: "Dezembro" },
];

// Formata número de crédito com vírgula decimal (ex.: 2.5 -> "2,5")
const fmt = (n) => {
  const v = Number(n ?? 0);
  return (Number.isInteger(v) ? v.toFixed(1) : v.toString()).replace(".", ",");
};

export default function AdminLeitorQR() {
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState("");
  const [mode, setMode] = useState("checkin"); // checkin | consulta
  const [open, setOpen] = useState(false);
  const [scans, setScans] = useState([]);
  const queueKey = "ceap_offline_queue";

  // Filtro de UF — persistido entre sessões (operador volta e mantém a escolha)
  const [ufFilter, setUfFilter] = useState(() => {
    try { return localStorage.getItem(STATE_FILTER_KEY) || "todos"; }
    catch { return "todos"; }
  });
  const [monthFilter, setMonthFilter] = useState(() => {
    try { return localStorage.getItem(MONTH_FILTER_KEY) || "todos"; } catch { return "todos"; }
  });
  const [yearFilter, setYearFilter] = useState(() => {
    try { return localStorage.getItem(YEAR_FILTER_KEY) || "todos"; } catch { return "todos"; }
  });
  useEffect(() => {
    try { localStorage.setItem(STATE_FILTER_KEY, ufFilter); } catch { /* ignore */ }
  }, [ufFilter]);
  useEffect(() => {
    try { localStorage.setItem(MONTH_FILTER_KEY, monthFilter); } catch { /* ignore */ }
  }, [monthFilter]);
  useEffect(() => {
    try { localStorage.setItem(YEAR_FILTER_KEY, yearFilter); } catch { /* ignore */ }
  }, [yearFilter]);

  useEffect(() => { api.get("/courses").then(r => setCourses(r.data || [])).catch(() => {}); }, []);

  // UFs disponíveis = só os estados que têm curso cadastrado (mantém a lista enxuta)
  const availableUFs = useMemo(() => {
    const set = new Set(courses.map((c) => (c.estado || "").toUpperCase()).filter(Boolean));
    return UFS.filter((u) => set.has(u.value));
  }, [courses]);

  // Anos disponíveis
  const availableYears = useMemo(() => {
    const set = new Set(
      courses.map(c => (c.start_date || "").slice(0, 4)).filter(y => /^\d{4}$/.test(y))
    );
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [courses]);

  // Cursos filtrados pela UF + mês + ano
  const filteredCourses = useMemo(() => {
    return courses.filter(c => {
      if (ufFilter !== "todos" && (c.estado || "").toUpperCase() !== ufFilter) return false;
      const sd = c.start_date || "";
      if (yearFilter !== "todos" && sd.slice(0, 4) !== yearFilter) return false;
      if (monthFilter !== "todos") {
        const mm = sd.slice(5, 7);
        const numMM = String(parseInt(mm || "0", 10));
        if (numMM !== monthFilter) return false;
      }
      return true;
    });
  }, [courses, ufFilter, monthFilter, yearFilter]);

  // Se a UF mudar e o curso selecionado não está na lista filtrada, limpa a seleção
  useEffect(() => {
    if (selected && !filteredCourses.find((c) => c.id === selected)) {
      setSelected("");
    }
  }, [filteredCourses, selected]);

  // Try sync offline queue
  useEffect(() => {
    const sync = async () => {
      try {
        const queue = JSON.parse(localStorage.getItem(queueKey) || "[]");
        if (!queue.length || !navigator.onLine) return;
        const remaining = [];
        for (const item of queue) {
          try {
            const { data } = await api.post("/attendance/scan", item);
            setScans(prev => [{ ok: true, data, when: new Date().toLocaleTimeString("pt-BR") }, ...prev]);
          } catch (e) {
            remaining.push(item);
          }
        }
        localStorage.setItem(queueKey, JSON.stringify(remaining));
        if (queue.length !== remaining.length) {
          toast.success(`Sincronizado: ${queue.length - remaining.length} leitura(s)`);
        }
      } catch { /* ignore sync failure */ }
    };
    sync();
    window.addEventListener("online", sync);
    return () => window.removeEventListener("online", sync);
  }, []);

  const handleDetected = async (text) => {
    if (!selected) {
      toast.error("Selecione um curso primeiro");
      return;
    }
    // ===== MODO CONSULTA (não registra presença) =====
    if (mode === "consulta") {
      try {
        const { data } = await api.post("/attendance/lookup", { qr_token: text, course_id: selected });
        setScans(prev => [{ ok: true, kind: "consulta", data, when: new Date().toLocaleTimeString("pt-BR") }, ...prev]);
        if (data.certificate_unlocked) {
          toast.success(`${data.student?.full_name}: frequência completa (${fmt(data.credits)}/${fmt(data.credits_required)}).`);
        } else {
          toast(`${data.student?.full_name}: ${fmt(data.credits)}/${fmt(data.credits_required)} — faltam ${fmt(data.credits_remaining)}.`, { duration: 6000 });
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || "Erro na consulta";
        setScans(prev => [{ ok: false, error: detail, when: new Date().toLocaleTimeString("pt-BR") }, ...prev]);
        toast.error(detail);
      }
      return;
    }
    // ===== MODO CHECK-IN =====
    const payload = { qr_token: text, course_id: selected, action: "checkin" };
    try {
      const { data } = await api.post("/attendance/scan", payload);
      setScans(prev => [{ ok: true, kind: "checkin", data, when: new Date().toLocaleTimeString("pt-BR") }, ...prev]);
      if (data.warning) {
        toast.warning(data.warning, { duration: 6500 });
      } else if (data.already_registered) {
        toast(data.message || "Check-in já registrado neste período.");
      } else {
        toast.success(`Check-in registrado — ${data.period_label || "período"} (${data.credit === 0.5 ? "meia entrada" : "entrada inteira"})`);
      }
      if (data.certificate_unlocked) {
        toast.success("Aluno atingiu 3,0 créditos! Certificado liberado (após a pesquisa de satisfação).", { duration: 6500 });
      }
      try {
        const a = new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=");
        const p = a.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch { /* audio blocked */ }
    } catch (err) {
      if (!navigator.onLine) {
        const queue = JSON.parse(localStorage.getItem(queueKey) || "[]");
        queue.push(payload); localStorage.setItem(queueKey, JSON.stringify(queue));
        setScans(prev => [{ ok: false, offline: true, payload, when: new Date().toLocaleTimeString("pt-BR") }, ...prev]);
        toast("Sem conexão. Salvo localmente.");
      } else {
        const detail = err?.response?.data?.detail || "Erro ao registrar";
        setScans(prev => [{ ok: false, error: detail, when: new Date().toLocaleTimeString("pt-BR") }, ...prev]);
        toast.error(detail);
      }
    }
  };

  return (
    <AdminLayout title="Leitor QR">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-xl"><CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Estado (UF)
              </p>
              <Select value={ufFilter} onValueChange={setUfFilter}>
                <SelectTrigger data-testid="qr-select-uf" className="h-9">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos ({courses.length})</SelectItem>
                  {availableUFs.map((uf) => {
                    const count = courses.filter((c) => (c.estado || "").toUpperCase() === uf.value).length;
                    return (
                      <SelectItem key={uf.value} value={uf.value} data-testid={`qr-uf-option-${uf.value}`}>
                        {uf.label} ({count})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Mês
              </p>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger data-testid="qr-select-month" className="h-9">
                  <SelectValue placeholder="Mês" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Ano
              </p>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger data-testid="qr-select-year" className="h-9">
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {availableYears.map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1 flex items-center justify-between">
              <span>Curso/Evento</span>
              {(ufFilter !== "todos" || monthFilter !== "todos" || yearFilter !== "todos") && (
                <span>
                  <strong>{filteredCourses.length}</strong> de {courses.length}
                </span>
              )}
            </p>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="qr-select-course">
                <SelectValue placeholder={
                  filteredCourses.length === 0
                    ? "Nenhum curso corresponde aos filtros"
                    : "Escolha o curso"
                } />
              </SelectTrigger>
              <SelectContent>
                {filteredCourses.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                    Ajuste os filtros acima para encontrar um curso.
                  </div>
                ) : (
                  filteredCourses.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.title} — {c.cidade}/{c.estado}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {(ufFilter !== "todos" || monthFilter !== "todos" || yearFilter !== "todos") && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Mostrando {filteredCourses.length} curso{filteredCourses.length !== 1 ? "s" : ""} com os filtros ativos.
                {" "}
                <button
                  type="button"
                  className="text-[hsl(var(--accent))] hover:underline"
                  onClick={() => { setUfFilter("todos"); setMonthFilter("todos"); setYearFilter("todos"); }}
                  data-testid="qr-clear-filters"
                >
                  Limpar filtros
                </button>
              </p>
            )}
          </div>
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="checkin" data-testid="qr-mode-checkin"><LogIn className="w-4 h-4 mr-1" /> Check-in</TabsTrigger>
              <TabsTrigger value="consulta" data-testid="qr-mode-consulta"><Search className="w-4 h-4 mr-1" /> Consultar</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3">
            {mode === "checkin" ? (
              <>
                <p className="text-sm font-medium flex items-center gap-1.5 text-[hsl(var(--primary))]">
                  <LogIn className="w-4 h-4" /> Registrar Check-in (entrada)
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  A <strong>saída é automática</strong>: manhã às <strong>12h</strong>, tarde às <strong>17h</strong>.
                  Entrada pontual (manhã até 10:30 / tarde até 15:00) vale <strong>1,0</strong>; entrada atrasada vale <strong>0,5 (meia entrada)</strong>.
                  O certificado é liberado ao somar <strong>3,0 créditos</strong>.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium flex items-center gap-1.5 text-[hsl(var(--primary))]">
                  <Search className="w-4 h-4" /> Consultar frequência do aluno
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Leia o QR do aluno para ver <strong>quantos créditos</strong> ele já tem e o que falta.
                  <strong> Não registra presença.</strong>
                </p>
              </>
            )}
          </div>
          <Button className="w-full h-12" disabled={!selected} onClick={() => setOpen(true)} data-testid="admin-qr-scanner">
            <ScanLine className="w-5 h-5 mr-2" /> {mode === "checkin" ? "Iniciar leitura (Check-in)" : "Consultar frequência"}
          </Button>
          <p className="text-xs text-muted-foreground">Funciona offline: leituras são sincronizadas quando voltar a conexão.</p>
        </CardContent></Card>

        <Card className="rounded-xl"><CardContent className="p-5">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] mb-3">Últimas leituras</h3>
          {scans.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma leitura ainda.</p>}
          <ul className="space-y-2" data-testid="admin-last-scans-list">
            {scans.slice(0, 12).map((s, idx) => {
              const isConsulta = s.kind === "consulta";
              const okGood = s.ok && !s.data?.is_late;
              return (
              <li key={idx} className="flex items-start gap-3 border border-[hsl(var(--border))] rounded-lg p-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${okGood || (s.ok && isConsulta) ? "bg-emerald-100 text-emerald-700" : (s.ok ? "bg-amber-100 text-amber-800" : "bg-amber-100 text-amber-800")}`}>
                  {s.ok && (isConsulta ? <Search className="w-4 h-4" /> : (okGood ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />))}
                  {!s.ok && <AlertTriangle className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  {s.ok && (
                    <>
                      <p className="text-sm font-medium">
                        {s.data?.student?.full_name}
                        {isConsulta && <span className="ml-1 text-[11px] font-normal text-muted-foreground">(consulta)</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {s.data?.student?.ceap_id}
                        {!isConsulta && s.data?.period_label ? ` • Entrada (${s.data.period_label})` : ""}
                        {" "}• {s.when}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {s.data?.credits != null && (
                          <Badge variant="secondary" data-testid="scan-credits-badge">
                            {fmt(s.data.credits)}/{fmt(s.data.credits_required || 3)} créditos
                          </Badge>
                        )}
                        {!isConsulta && s.data?.credit != null && (
                          <Badge variant="outline">{s.data.credit === 0.5 ? "Meia entrada (0,5)" : "Entrada inteira (1,0)"}</Badge>
                        )}
                        {s.data?.is_late && !isConsulta && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200">Atrasado</Badge>
                        )}
                        {!s.data?.certificate_unlocked && s.data?.credits_remaining != null && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Faltam {fmt(s.data.credits_remaining)}</Badge>
                        )}
                        {s.data?.already_registered && (
                          <Badge variant="outline">Já registrado neste período</Badge>
                        )}
                        {s.data?.certificate_unlocked && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Certificado liberado</Badge>
                        )}
                      </div>
                      {isConsulta && Array.isArray(s.data?.breakdown) && s.data.breakdown.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {s.data.breakdown.map((b, i) => (
                            <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded border ${b.is_late ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                              {b.date?.slice(5)} {b.period_label} {b.is_late ? "½" : "•"}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {!s.ok && <p className="text-sm">{s.offline ? "Salvo offline" : (s.error || "Falha")}</p>}
                </div>
              </li>
              );
            })}
          </ul>
        </CardContent></Card>
      </div>
      <QrScannerSheet open={open} onOpenChange={setOpen} onDetected={handleDetected} title={mode === "checkin" ? "Check-in" : "Consultar frequência"} />
    </AdminLayout>
  );
}
